// modules/reportePagos/pages/ReportePagosDetalleCobrosPage.jsx
// Migracion del "index.php" de reporte_pagos (listado detallado de cobros de
// facturas y franquicias por mes/año/sucursal). El backend ya resuelve en
// una sola consulta lo que el PHP original hacia con 3-4 consultas extra por
// cada fila (nombre de banco, series de venta/franquicia, contrato vigente)
// — ver reportePagosModel.getDetalleCobros.
//
// El filtro de "Banco de Depósito" cubre lo que en el reporte legado era un
// boton aparte ("Reporte por Banco:", busqueda.php -> siete) — como las
// columnas que necesitaba ese reporte son las mismas que ya trae este
// listado, se sumo como un filtro mas en vez de duplicar toda la tabla en
// otra pagina.
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, TextField, MenuItem, Typography, Alert, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  TableSortLabel, Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { getDetalleCobros, getBancosActivos, exportarDetalleCobros } from "../services/reportePagosService";

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.72rem",
  whiteSpace: "nowrap",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};

const celdaTruncada = { maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const SUCURSALES = ["Todo", "La Paz", "Cochabamba", "Santa Cruz"];
const MESES = [
  { value: "Todo", label: "Todo (año completo)" },
  { value: 1, label: "Enero" }, { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" }, { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" }, { value: 6, label: "Junio" },
  { value: 7, label: "Julio" }, { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" }, { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" },
];

const anioActual = new Date().getFullYear();
const ANIOS = Array.from({ length: 12 }, (_, i) => anioActual + 1 - i);

const formatMoneda = (value) =>
  Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatFecha = (value) => {
  const m = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "-";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const COLUMNAS = [
  { col: "num", label: "Num" },
  { col: "cliente", label: "Cliente" },
  { col: "grupo", label: "Grupo" },
  { col: "nombreServicio", label: "Nombre" },
  { col: "descripcion", label: "Descrip. Serv." },
  { col: "servicio", label: "Servicio" },
  { col: "fechaFactura", label: "Fecha Emisión" },
  { col: "tipoIngreso", label: "Tipo de Ingreso" },
  { col: "numFactura", label: "N° Factura" },
  { col: "numRecibo", label: "N° Recibo" },
  { col: "numCheque", label: "N° Cheque" },
  { col: "mesServicio", label: "Mes de Servicio" },
  { col: "bolivianos", label: "Monto Bs", align: "right" },
  { col: "dolares", label: "Monto $us", align: "right" },
  { col: "codBanco", label: "Cod. Banco" },
  { col: "bancoPago", label: "Banco Pago" },
  { col: "bancoDeposito", label: "Banco Depósito" },
  { col: "fechaDeposito", label: "Fecha Depósito" },
  { col: "observacion", label: "Observación" },
  { col: "sucursal", label: "Sucursal" },
  { col: "cobrador", label: "Cobrador" },
  { col: "comision", label: "Comi" },
  { col: "ejecutivoVenta", label: "Ejecutivo Venta" },
  { col: "fechaFinalizacionContrato", label: "Fin Contrato" },
  { col: "cuf", label: "CUF" },
  { col: "vinculado", label: "Vinculado" },
];

const getValorOrdenable = (row, col) => {
  if (col === "fechaFactura" || col === "fechaDeposito") {
    return row[col] ? new Date(row[col]).getTime() : 0;
  }
  if (col === "series") return (row.series || []).join(", ");
  return row[col];
};

// Campos de texto/numero sobre los que busca el cuadro "Buscar" — cubre lo
// que se ve en la tabla (cliente, servicio, facturas/recibos/cheques,
// observacion, etc.) para no obligar a saber en que columna esta el dato.
const CAMPOS_BUSCADOS = [
  "num", "cliente", "grupo", "nombreServicio", "descripcion", "servicio",
  "tipoIngreso", "numFactura", "numRecibo", "numCheque", "mesServicio",
  "codBanco", "bancoPago", "bancoDeposito", "observacion", "sucursal",
  "cobrador", "ejecutivoVenta", "cuf",
];

const coincideBusqueda = (row, texto) => {
  if (!texto) return true;
  const t = texto.trim().toLowerCase();
  if (!t) return true;
  if (CAMPOS_BUSCADOS.some((campo) => String(row[campo] ?? "").toLowerCase().includes(t))) return true;
  return (row.series || []).some((s) => String(s ?? "").toLowerCase().includes(t));
};

const compararValores = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === "boolean" || typeof b === "boolean") return (a ? 1 : 0) - (b ? 1 : 0);
  if (typeof a === "string" || typeof b === "string") {
    return String(a).localeCompare(String(b), "es", { numeric: true, sensitivity: "base" });
  }
  return a - b;
};

const SortableHeaderCell = ({ col, label, align, orderBy, orderDir, onSort }) => (
  <StyledHeaderCell align={align} sortDirection={orderBy === col ? orderDir : false}>
    <TableSortLabel
      active={orderBy === col}
      direction={orderBy === col ? orderDir : "asc"}
      onClick={() => onSort(col)}
    >
      {label}
    </TableSortLabel>
  </StyledHeaderCell>
);

const ReportePagosDetalleCobrosPage = () => {
  const [sucursal, setSucursal] = useState("Todo");
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(anioActual);
  const [banco, setBanco] = useState("");
  const [bancos, setBancos] = useState([]);
  const [items, setItems] = useState([]);
  const [totales, setTotales] = useState({ bolivianos: 0, dolares: 0 });
  const [buscado, setBuscado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [error, setError] = useState("");
  const [orderBy, setOrderBy] = useState(null);
  const [orderDir, setOrderDir] = useState("asc");
  // Filtro de texto sobre lo ya cargado (no vuelve a pedirle nada al backend).
  const [textoBusqueda, setTextoBusqueda] = useState("");

  useEffect(() => {
    getBancosActivos().then(({ data }) => setBancos(Array.isArray(data) ? data : [])).catch(() => setBancos([]));
  }, []);

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrderDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(col);
      setOrderDir("asc");
    }
  };

  const itemsFiltrados = useMemo(
    () => items.filter((row) => coincideBusqueda(row, textoBusqueda)),
    [items, textoBusqueda]
  );

  const itemsOrdenados = useMemo(() => {
    if (!orderBy) return itemsFiltrados;
    const copia = [...itemsFiltrados].sort((a, b) =>
      compararValores(getValorOrdenable(a, orderBy), getValorOrdenable(b, orderBy))
    );
    if (orderDir === "desc") copia.reverse();
    return copia;
  }, [itemsFiltrados, orderBy, orderDir]);

  // Con filtro de texto activo, los totales de abajo pasan a sumar solo lo
  // filtrado (si no, se muestran los totales exactos que ya calculo el backend).
  const totalesMostrados = useMemo(() => {
    if (!textoBusqueda.trim()) return totales;
    return itemsFiltrados.reduce(
      (acc, r) => ({
        bolivianos: acc.bolivianos + Number(r.bolivianos || 0),
        dolares: acc.dolares + Number(r.dolares || 0),
      }),
      { bolivianos: 0, dolares: 0 }
    );
  }, [textoBusqueda, itemsFiltrados, totales]);

  const handleBuscar = async () => {
    setLoading(true);
    setError("");
    setTextoBusqueda("");
    try {
      const { data } = await getDetalleCobros({ sucursal, year, mes, banco: banco || undefined });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotales(data?.totales || { bolivianos: 0, dolares: 0 });
      setBuscado(true);
    } catch (e) {
      setItems([]);
      setError(e?.response?.data?.error || "No se pudo generar el reporte.");
    } finally {
      setLoading(false);
    }
  };

  const handleExportar = async () => {
    setExportando(true);
    setError("");
    try {
      const response = await exportarDetalleCobros({ sucursal, year, mes, banco: banco || undefined });
      const url = URL.createObjectURL(new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "detalle_cobros.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo exportar el reporte.");
    } finally {
      setExportando(false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Detalle de Cobros"
        subtitle="Cobros de facturas y franquicias, fila por fila, por sucursal/mes/año"
        backTo="/reporte-facturas"
      />
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
          <TextField
            select size="small" label="Sucursal" sx={{ ...compactFieldSx, width: 150 }}
            value={sucursal} onChange={(e) => setSucursal(e.target.value)}
          >
            {SUCURSALES.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: "0.85rem" }}>{s}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Mes" sx={{ ...compactFieldSx, width: 190 }}
            value={mes} onChange={(e) => setMes(e.target.value)}
          >
            {MESES.map((m) => <MenuItem key={m.value} value={m.value} sx={{ fontSize: "0.85rem" }}>{m.label}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Año" sx={{ ...compactFieldSx, width: 110 }}
            value={year} onChange={(e) => setYear(Number(e.target.value))}
          >
            {ANIOS.map((a) => <MenuItem key={a} value={a} sx={{ fontSize: "0.85rem" }}>{a}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Banco de Depósito" sx={{ ...compactFieldSx, width: 200 }}
            value={banco} onChange={(e) => setBanco(e.target.value)}
          >
            <MenuItem value="" sx={{ fontSize: "0.85rem" }}>Todos los bancos</MenuItem>
            {bancos.map((b) => <MenuItem key={b.id} value={b.id} sx={{ fontSize: "0.85rem" }}>{b.nombre}</MenuItem>)}
          </TextField>
          <Button
            size="small" variant="contained"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
            onClick={handleBuscar} disabled={loading} sx={{ minHeight: 32 }}
          >
            {loading ? "Buscando..." : "Buscar"}
          </Button>
          {buscado && items.length > 0 && (
            <Button
              size="small" variant="outlined"
              startIcon={exportando ? <CircularProgress size={14} /> : <FileDownloadIcon />}
              onClick={handleExportar} disabled={exportando} sx={{ minHeight: 32 }}
            >
              {exportando ? "Exportando..." : "Exportar Excel"}
            </Button>
          )}
          {buscado && items.length > 0 && (
            <TextField
              size="small" placeholder="Buscar (cliente, factura, recibo, cheque, observación...)"
              value={textoBusqueda} onChange={(e) => setTextoBusqueda(e.target.value)}
              sx={{ ...compactFieldSx, width: 300 }}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} /> }}
            />
          )}
          {buscado && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              Total: {textoBusqueda.trim() ? `${itemsFiltrados.length} de ${items.length}` : items.length}
            </Typography>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!buscado ? (
          <Alert severity="info">Elige sucursal, mes y año, y presiona Buscar.</Alert>
        ) : !loading && items.length === 0 ? (
          <Alert severity="info">No hay cobros para los filtros seleccionados.</Alert>
        ) : !loading && itemsFiltrados.length === 0 ? (
          <Alert severity="info">Ningún cobro coincide con "{textoBusqueda}".</Alert>
        ) : (
          <>
            <Paper variant="outlined">
              <TableContainer sx={{ maxHeight: "65vh" }}>
                <Table size="small" stickyHeader sx={{ "& td": { py: 0.35, px: 0.75, fontSize: "0.74rem", whiteSpace: "nowrap" } }}>
                  <TableHead>
                    <TableRow>
                      {COLUMNAS.map((c) => (
                        <SortableHeaderCell key={c.col} {...c} orderBy={orderBy} orderDir={orderDir} onSort={handleSort} />
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={COLUMNAS.length} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : (
                      itemsOrdenados.map((r) => (
                        <TableRow key={r.num} hover>
                          <TableCell>{r.num}</TableCell>
                          <TableCell sx={celdaTruncada} title={r.cliente}>{r.cliente}</TableCell>
                          <TableCell>{r.grupo || "-"}</TableCell>
                          <TableCell sx={celdaTruncada} title={r.nombreServicio}>{r.nombreServicio || "-"}</TableCell>
                          <TableCell sx={celdaTruncada} title={r.descripcion}>{r.descripcion}</TableCell>
                          <TableCell>{r.servicio}</TableCell>
                          <TableCell>{formatFecha(r.fechaFactura)}</TableCell>
                          <TableCell sx={celdaTruncada} title={[r.tipoIngreso, ...(r.series || [])].join(" — ")}>
                            {r.tipoIngreso}{r.series?.length ? ` — ${r.series.join(", ")}` : ""}
                          </TableCell>
                          <TableCell>{r.numFactura || "-"}</TableCell>
                          <TableCell>{r.numRecibo || "-"}</TableCell>
                          <TableCell>{r.numCheque || "-"}</TableCell>
                          <TableCell>{r.mesServicio}</TableCell>
                          <TableCell align="right">{formatMoneda(r.bolivianos)}</TableCell>
                          <TableCell align="right">{formatMoneda(r.dolares)}</TableCell>
                          <TableCell>{r.codBanco || "-"}</TableCell>
                          <TableCell>{r.bancoPago || "-"}</TableCell>
                          <TableCell>{r.bancoDeposito || "-"}</TableCell>
                          <TableCell>{formatFecha(r.fechaDeposito)}</TableCell>
                          <TableCell sx={celdaTruncada} title={r.observacion}>{r.observacion || "-"}</TableCell>
                          <TableCell>{r.sucursal}</TableCell>
                          <TableCell>{r.cobrador || "-"}</TableCell>
                          <TableCell>{r.comision || "-"}</TableCell>
                          <TableCell>{r.ejecutivoVenta || "-"}</TableCell>
                          <TableCell>{r.fechaFinalizacionContrato ? formatFecha(r.fechaFinalizacionContrato) : "-"}</TableCell>
                          {/* CUF completo (se copia o compara con el SIN), en monoespaciada para que ocupe menos y se lea mejor. */}
                          <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem !important", userSelect: "all" }}>{r.cuf || "-"}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={r.vinculado ? "Vinculado" : "Sin vincular"}
                              color={r.vinculado ? "success" : "default"}
                              variant={r.vinculado ? "filled" : "outlined"}
                              sx={{ fontSize: "0.68rem", height: 20 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, maxWidth: 320, ml: "auto" }}>
              {textoBusqueda.trim() && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  Filtrado por "{textoBusqueda}"
                </Typography>
              )}
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Total bolivianos</Typography>
                <Typography variant="body2" fontWeight={700}>{formatMoneda(totalesMostrados.bolivianos)}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                <Typography variant="body2">Total dólares</Typography>
                <Typography variant="body2" fontWeight={700}>{formatMoneda(totalesMostrados.dolares)}</Typography>
              </Box>
            </Paper>
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ReportePagosDetalleCobrosPage;
